// Dev-only mock (TZ §8.3): renders the UI in a plain browser without the Brain
// backend or Telegram. Enabled with ?mock=1 on a DEV build only — `MOCK` is
// always false in production bundles (import.meta.env.DEV === false), so these
// fixtures and the short-circuits that read them are dead code in prod.
import type {
  AiInsightResponse,
  AiHomeResponse,
  AiOverviewResponse,
  AiReportsResponse,
  ArchiveReportDetail,
  ArchiveReportsResponse,
  AuthResponse,
  CallDetailResponse,
  CallsListResponse,
  CriticalDealsResponse,
  DashboardResponse,
  DaySummary,
  DealAttachmentsResponse,
  DealCallsResponse,
  DealDetailResponse,
  DealsListResponse,
  MeResponse,
  NotificationPrefs,
  OverdueResponse,
  ReactivationResponse,
  SearchResponse,
  TaskDetailResponse,
  TeamMemberDetailResponse,
  TeamResponse,
  TodayResponse,
} from '../api/types'
import type { RecommendationsResponse } from '../api/recommendations'

const params = typeof location !== 'undefined' ? new URLSearchParams(location.search) : new URLSearchParams()

export const MOCK = import.meta.env.DEV && params.has('mock')

// ?theme=dark forces the dark palette for the dev preview. Exposed as a
// function (not run at import) so main.tsx can apply it AFTER the Telegram
// SDK's applyTheme() — telegram-web-app.js defines window.Telegram even in a
// plain browser and resets the class to its (light) colorScheme on init.
export function applyMockTheme(): void {
  // Dev-only ?theme=dark override — works for both the fixture mock and the
  // real-backend dev URL (?mock_init_data=…), independent of MOCK.
  if (import.meta.env.DEV && params.get('theme') === 'dark') {
    document.documentElement.classList.add('theme-dark')
  }
}

// ?mock=picker exercises the multi-tenant picker screen.
const multiTenant = MOCK && params.get('mock') === 'picker'

export const mockAuth: AuthResponse = {
  jwt: 'mock.jwt.token',
  user: {
    telegram_user_id: 555,
    first_name: 'Бекзод',
    last_name: 'Каримов',
    username: 'bekzod_k',
    language_code: 'ru',
  },
  memberships: multiTenant
    ? [
        { tenant_id: 1, tenant_name: 'Worldunicorns', bitrix_user_id: 928, role: 'manager' },
        { tenant_id: 4, tenant_name: 'Silk Road Logistics', bitrix_user_id: 1042, role: 'director' },
      ]
    : [{ tenant_id: 1, tenant_name: 'Worldunicorns', bitrix_user_id: 928, role: 'manager' }],
  active_tenant_id: 1,
}

// GET /me — carries the tenant's working hours so the dev work-day header shows
// real saved hours (10:00–18:00, Пн–Сб) instead of the design's 10:00–17:00.
export const mockMe: MeResponse = {
  telegram_user_id: mockAuth.user.telegram_user_id,
  active_tenant: { id: 1, name: 'Worldunicorns', bitrix_user_id: 928, role: 'manager' },
  all_memberships: mockAuth.memberships,
  settings: { language: 'ru', tz_name: 'Asia/Tashkent' },
  features: { insights: true, ask_ai: true, draft_reply: true, reactivation: true, ai_tab: true, ai_digest: true, call_analysis: true },
  work_hours: {
    start_h: 10, end_h: 18, days: [0, 1, 2, 3, 4, 5],
    lunch_enabled: true, lunch_start: [13, 0], lunch_end: [13, 30],
    tz_name: 'Asia/Tashkent',
  },
}

export const mockToday: TodayResponse = {
  today_label: '29 мая, пятница',
  now_hhmm: '14:48',
  summary: { total: 11, overdue: 1, duesoon: 2, pending: 8, done: 4 },
  tasks: [
    { id: 1986, deal_id: 1986, title: 'Позвонить клиенту', client_name: 'Бекзод Каримов', status: 'overdue', type: 'call', channel: 'telegram_business', deadline_iso: '2026-05-29T14:30:00+05:00', deadline_label: 'Сегодня 14:30', deadline_min_delta: -18, escalation_level: 2, stage_label: 'TG-MALUMOT', from_ai: true, ai: null },
    { id: 1991, deal_id: 1991, title: 'Отправить тариф на сборный груз', client_name: 'Дилноза Рашидова', status: 'duesoon', type: 'message', channel: 'telegram', deadline_iso: '2026-05-29T15:30:00+05:00', deadline_label: 'Сегодня 15:30', deadline_min_delta: 42, escalation_level: 0, stage_label: 'MALUMOT SORALDI', from_ai: true, ai: null },
    { id: 2004, deal_id: 2004, title: 'Перезвонить по доставке Гуанчжоу', client_name: 'Олим Турсунов', status: 'duesoon', type: 'call', channel: 'call', deadline_iso: '2026-05-29T16:00:00+05:00', deadline_label: 'Сегодня 16:00', deadline_min_delta: 72, escalation_level: 0, stage_label: 'TEL QOLDIRDI', from_ai: false, ai: null },
    { id: 2010, deal_id: 2010, title: 'Уточнить адрес склада в Ташкенте', client_name: 'Сардор Маннонов', status: 'pending', type: 'message', channel: 'instagram', deadline_iso: '2026-05-29T17:00:00+05:00', deadline_label: 'Сегодня 17:00', deadline_min_delta: 132, escalation_level: 0, stage_label: 'COMMENTLA', from_ai: false, ai: null },
    { id: 2021, deal_id: 2021, title: 'Согласовать счёт-фактуру', client_name: 'Жасур Ё.', status: 'pending', type: 'email', channel: 'telegram', deadline_iso: '2026-05-29T17:30:00+05:00', deadline_label: 'Сегодня 17:30', deadline_min_delta: 162, escalation_level: 0, stage_label: 'ESLATMA', from_ai: false, ai: null },
  ],
}

// End-of-day KPI summary fixture (Состояния интерфейса · endofday). Mirrors the
// honest backend: done/avg_score real, reaction/hot null → "—".
export const mockDaySummary: DaySummary = {
  done: 11,
  avg_score: 4.7,
  reaction_seconds: null,
  hot_leads: null,
}

// Preview the interface-states on Today via ?mock=1&mock_state=empty|endofday|crmdown.
// Dead in prod (DEV-only). Returns a tailored TodayResponse for each state.
export function mockTodayState(): TodayResponse {
  const state = params.get('mock_state')
  const emptySummary = { total: 0, overdue: 0, duesoon: 0, pending: 0, done: 0 }
  if (state === 'empty') {
    return { ...mockToday, tasks: [], summary: emptySummary }
  }
  if (state === 'endofday') {
    return { ...mockToday, tasks: [], summary: { ...emptySummary, done: 11 } }
  }
  if (state === 'crmdown') {
    return { ...mockToday, crm_ok: false }
  }
  return mockToday
}

// «План на завтра» fixture — what GET /api/miniapp/tomorrow returns. A few
// next-day tasks so the bottom sheet is demoable from the empty / end-of-day
// states (?mock=1&mock_state=empty|endofday → tap «Посмотреть план на завтра»).
export const mockTomorrow: TodayResponse = {
  today_label: 'Завтра, 30 мая, суббота',
  now_hhmm: '14:48',
  summary: { total: 3, overdue: 0, duesoon: 0, pending: 3, done: 0 },
  tasks: [
    { id: 2101, deal_id: 2101, title: 'Позвонить по растаможке груза', client_name: 'Нодира Юлдашева', status: 'pending', type: 'call', channel: 'call', deadline_iso: '2026-05-30T10:00:00+05:00', deadline_label: 'Завтра 10:00', deadline_min_delta: 1380, escalation_level: 0, stage_label: 'TEL QOLDIRDI', from_ai: true, ai: null },
    { id: 2102, deal_id: 2102, title: 'Отправить расчёт доставки', client_name: 'Сардор Маннонов', status: 'pending', type: 'message', channel: 'telegram', deadline_iso: '2026-05-30T12:00:00+05:00', deadline_label: 'Завтра 12:00', deadline_min_delta: 1500, escalation_level: 0, stage_label: 'MALUMOT SORALDI', from_ai: true, ai: null },
    { id: 2103, deal_id: 2103, title: 'Согласовать договор', client_name: 'Дилноза Рашидова', status: 'pending', type: 'document', channel: 'telegram', deadline_iso: '2026-05-30T15:00:00+05:00', deadline_label: 'Завтра 15:00', deadline_min_delta: 1680, escalation_level: 0, stage_label: 'ESLATMA', from_ai: false, ai: null },
  ],
}

export const mockOverdue: OverdueResponse = {
  tasks: mockToday.tasks.filter((tk) => tk.status === 'overdue'),
  summary: { total: 1 },
}

// ── M-3 director fixtures (render the director UI with ?mock=1&mock_role=director) ──

const trend14 = (base: number) =>
  Array.from({ length: 14 }, (_, i) => ({
    day: `2026-05-${String(16 + i).padStart(2, '0')}`,
    value: Math.max(0, Math.round(base + 3 * Math.sin(i / 1.7) + (i % 4 === 0 ? 2 : 0))),
  }))

export const mockDashboard: DashboardResponse = {
  period: 'today',
  kpi: {
    open_tasks: { value: 47, delta_wow: null },
    overdue: { value: 8, delta_wow: -2 },
    completed: { value: 23, delta_wow: 5 },
    avg_score: { value: 8.2, delta_wow: -0.3 },
  },
  tenant_trend: { metric: 'overdue', points: trend14(6) },
  worst_3: [
    { manager_id: 928, name: 'Нафиса Собирова', open: 14, overdue: 5, red: 2, avg_score: 7.4, delta_overdue: 3, inactive: false },
    { manager_id: 1031, name: 'Олим Турсунов', open: 11, overdue: 3, red: 1, avg_score: 7.9, delta_overdue: 1, inactive: false },
    { manager_id: 1044, name: 'Жасур Ёдгоров', open: 9, overdue: 2, red: 1, avg_score: 8.0, delta_overdue: -1, inactive: false },
  ],
  best_3: [
    { manager_id: 1026, name: 'Тохир Тошматов', open: 6, overdue: 0, red: 0, avg_score: 9.3, delta_overdue: -2, inactive: false },
    { manager_id: 1029, name: 'Дилноза Рашидова', open: 7, overdue: 1, red: 0, avg_score: 9.0, delta_overdue: 0, inactive: false },
    { manager_id: 1052, name: 'Сардор Маннонов', open: 5, overdue: 0, red: 0, avg_score: 8.8, delta_overdue: -1, inactive: false },
  ],
  critical_deals_preview: [
    { id: 1986, client_name: 'Бекзод Каримов', reason: 'Просрочка 2 дня · крупная' },
    { id: 2098, client_name: 'Азиз Рахимов', reason: 'Заморожена 6 дней' },
    { id: 2110, client_name: 'Малика Юсупова', reason: 'Сумма ≥ 10 млн' },
  ],
  summary: { critical_deals_count: 5, active_managers: 5, total_managers: 6 },
  ai_digest: {
    text: 'За сегодня закрыто 23 задачи (+5 к среднему). У Нафисы 5 просрочек (▲3) — стоит разгрузить. 5 сделок в зоне риска.',
    generated_at: '2026-05-29T09:00:00+05:00',
  },
}

const teamCard = (
  id: number, name: string, open: number, overdue: number, done: number,
  score: number, status: 'active' | 'offline', base: number, inactive = false,
) => ({
  id, name,
  short_name: `${name.split(' ')[0]} ${name.split(' ')[1]?.[0] ?? ''}.`,
  avatar_initials: name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase(),
  status,
  stats: { open_tasks: open, overdue, done_period: done, avg_score: score, load_pct: Math.min(100, Math.round((open / 12) * 100)), norm: 12 },
  trend: trend14(base).map((p) => ({ ...p, value: Math.max(0, Math.round((p.value ?? 0) / 2 + score - 4)) })),
  delta: { red: overdue > 3 ? 2 : 0, overdue: overdue > 3 ? 3 : -1, done: done > 10 ? 5 : 2, avg_score: score > 8.5 ? 0.2 : -0.4 },
  inactive_this_window: inactive,
  local: false,
  role: null,
  telegram_chat_id: null,
})

export const mockTeam: TeamResponse = {
  managers: [
    teamCard(928, 'Нафиса Собирова', 14, 5, 12, 7.4, 'active', 6),
    teamCard(1026, 'Тохир Тошматов', 6, 0, 14, 9.3, 'active', 3),
    teamCard(1029, 'Дилноза Рашидова', 7, 1, 11, 9.0, 'active', 4),
    teamCard(1031, 'Олим Турсунов', 11, 3, 9, 7.9, 'offline', 5),
    teamCard(1044, 'Жасур Ёдгоров', 9, 2, 8, 8.0, 'active', 4),
    teamCard(1052, 'Сардор Маннонов', 5, 0, 7, 8.8, 'offline', 2, true),
    // A local employee who hasn't opened the invite yet (offline + no tasks) →
    // shows the «локальный» badge + «ещё не подключён».
    {
      id: -1001, local: true, name: 'Иван', short_name: 'Иван', avatar_initials: 'И',
      status: 'offline' as const,
      stats: { open_tasks: 0, overdue: 0, done_period: 0, avg_score: null, load_pct: 0, norm: 12 },
      trend: [], delta: { red: 0, overdue: 0, done: 0, avg_score: null },
      inactive_this_window: true,
      role: 'manager' as const, telegram_chat_id: '',
    },
  ],
}

// Director CreateTask writes (POST). Parse echoes the instruction into a
// plausible structured draft; create just acks.
export function mockParseTask(text: string) {
  const t = (text || '').toLowerCase()
  const type = /встреч|meeting|uchrash/.test(t) ? 'meeting'
    : /сообщ|напиш|message|xabar/.test(t) ? 'message'
    : /кп|документ|doc|hujjat/.test(t) ? 'doc' : 'call'
  const priority = /срочн|очень важно|urgent|juda muhim|жуда муҳим/.test(t) ? 'urgent' : 'high'
  return {
    manager_id: null,
    task_type: type,
    client: '',
    deal_id: null,
    deadline_label: 'Завтра 15:00',
    priority,
    conditions: [] as string[],
    comment: (text || '').trim().slice(0, 120),
  }
}

export const mockCreateTask = { ok: true, task_id: 9999, manager_id: 1026, bitrix_sync_failed: false }

export const mockTeamMember: TeamMemberDetailResponse = {
  manager: mockTeam.managers[0],
  open_tasks: [
    { id: 1986, deal_id: 1986, title: 'Позвонить клиенту', status: 'overdue', deadline_iso: '2026-05-29T14:30:00+05:00', escalation_level: 2 },
    { id: 1991, deal_id: 1991, title: 'Отправить тариф на сборный груз', status: 'duesoon', deadline_iso: '2026-05-29T15:30:00+05:00', escalation_level: 0 },
    { id: 2010, deal_id: 2010, title: 'Уточнить адрес склада', status: 'pending', deadline_iso: '2026-05-29T17:00:00+05:00', escalation_level: 0 },
  ],
  activity: [
    { at: '2026-05-29T13:10:00+05:00', kind: 'completed', task_id: 1900, title: 'Перезвонить по доставке' },
    { at: '2026-05-29T11:42:00+05:00', kind: 'assigned', task_id: 1991, title: 'Отправить тариф на сборный груз' },
    { at: '2026-05-28T18:05:00+05:00', kind: 'completed', task_id: 1880, title: 'Согласовать счёт' },
  ],
}

export const mockCritical: CriticalDealsResponse = {
  deals: [
    { id: 1986, client_name: 'Бекзод Каримов', stage: { id: 'UC_EYC73M', label: 'TG-MALUMOT' }, stage_days: 4, amount: 18500000, currency: 'UZS', manager: { id: 928, name: 'Нафиса С.' }, ai_intent: null, ai_confidence: null, reasons: [{ kind: 'overdue', days: 2 }, { kind: 'big', amount: 18500000 }], priority_score: 80 },
    { id: 2098, client_name: 'Азиз Рахимов', stage: { id: 'UC_X1', label: 'MALUMOT SORALDI' }, stage_days: 6, amount: 4200000, currency: 'UZS', manager: { id: 1031, name: 'Олим Т.' }, ai_intent: null, ai_confidence: null, reasons: [{ kind: 'frozen', days: 6 }], priority_score: 48 },
    { id: 2110, client_name: 'Малика Юсупова', stage: { id: 'UC_X2', label: 'TEL QOLDIRDI' }, stage_days: 3, amount: 12500000, currency: 'UZS', manager: { id: 1044, name: 'Жасур Ё.' }, ai_intent: null, ai_confidence: null, reasons: [{ kind: 'big', amount: 12500000 }], priority_score: 30 },
  ],
}

export const mockAiReports: AiReportsResponse = {
  reports: [
    {
      id: 'leader_2026-05-29',
      kind: 'daily_leader_digest',
      date: '2026-05-29',
      excerpt: 'Performance-алёрт по «Worldunicorns». Окно: последние 7 дн. Нафиса — 🔴 5 · просроч. 3. Олим — 🔴 1 · просроч. 3.',
      body_md: [
        '🚨 <b>Performance-алёрт по «Worldunicorns»</b>',
        'Окно: последние <b>7</b> дн. · порог: 🔴≥3, просрочек≥5',
        '',
        '🔥 <b>Нафиса Собирова</b> — 🔴 5 · просроч. 3 · ср.балл 7.4/10  <i>(к пр. периоду: 🔴 ▲2, просроч.▲3)</i>',
        '⚠️ <b>Олим Турсунов</b> — 🔴 1 · просроч. 3 · ср.балл 7.9/10',
        '',
        'Открыть в админке: /admin/tenants/1/managers?days=7',
      ].join('\n'),
      generated_at: '2026-05-29T09:00:00+05:00',
      sent_to_chat: true,
    },
    {
      id: 'leader_2026-05-22',
      kind: 'weekly_leader_digest',
      date: '2026-05-22',
      excerpt: 'Недельный обзор W21. Команда закрыла 142 задачи, средний балл 8.4. Просрочки выросли на 12%.',
      body_md: [
        '📊 <b>Недельный обзор · W21</b>',
        '',
        'Закрыто задач: <b>142</b> · средний балл <b>8.4/10</b>',
        'Просрочки: <b>+12%</b> к прошлой неделе',
      ].join('\n'),
      generated_at: '2026-05-22T09:00:00+05:00',
      sent_to_chat: true,
    },
  ],
}

export const mockAiOverview: AiOverviewResponse = {
  week_summary: {
    text: 'Команда закрыла на 14% больше сделок, но среднее время реакции выросло. Проблема — у Toxir T (23 мин), рекомендую разговор.',
    generated_at: '2026-06-02T09:00:00+05:00',
  },
  call_quality: { avg: 8.4, count: 27 },
  leads: { hot: 38, warm: 142, cold: 215 },
  top_objections: [
    { label: 'Цена выше конкурентов', n: 12 },
    { label: 'Долгий срок доставки', n: 8 },
    { label: 'Нужно согласование', n: 5 },
    { label: 'Уже есть поставщик', n: 4 },
  ],
  recent_calls: [
    { id: 'cq-2056', deal_id: 2056, client: 'Бекзод Каримов', score: 9, interest: 'hot', at: '2026-06-02T11:20:00+05:00' },
    { id: 'cq-2041', deal_id: 2041, client: 'Дилшод Рахимов', score: 6, interest: 'warm', at: '2026-06-02T09:05:00+05:00' },
    { id: 'cq-1990', deal_id: 1990, client: '#1990', score: 4, interest: 'cold', at: '2026-06-01T16:40:00+05:00' },
  ],
  managers: [
    { name: 'Нафиса Собирова', done: 12, quality: 7.4 },
    { name: 'Тохир Тошматов', done: 14, quality: 9.3 },
    { name: 'Дилноза Рашидова', done: 11, quality: 9.0 },
    { name: 'Олим Турсунов', done: 9, quality: 7.9 },
  ],
}

export const mockAiHome: AiHomeResponse = {
  day_summary: { calls: 12, hot: 4, objections: 5, text: null },
  categories: { calls: 6, objections: 5, hot: 4, reactivate: 9, followup: null, quality: 9.2 },
  top_objections: [
    { label: 'Дорого', n: 17 },
    { label: 'Долгие сроки', n: 14 },
    { label: 'У конкурента дешевле', n: 11 },
    { label: 'Подумаю', n: 8 },
    { label: 'Нет груза сейчас', n: 5 },
  ],
  hot_leads: [
    { deal_id: 1986, why: 'Спрашивал про оплату 2 раза за день' },
    { deal_id: 2056, why: 'Сама инициировала созвон' },
    { deal_id: 1972, why: 'Готова оплатить отгрузку сегодня' },
  ],
}

export const mockDeals: DealsListResponse = {
  deals: [
    { id: 1986, title: 'Сборный груз Гуанчжоу', client_name: 'Бекзод Каримов', stage: { id: 'UC_EYC73M', label: 'TG-MALUMOT' }, amount: 18500000, currency: 'UZS', open_tasks_count: 3, overdue_tasks_count: 1, ai_intent: 'hot', last_activity_at: '2026-05-29T12:00:00+05:00' },
    { id: 1991, title: 'Доставка оборудования', client_name: 'Дилноза Рашидова', stage: { id: 'UC_X1', label: 'MALUMOT SORALDI' }, amount: 7200000, currency: 'UZS', open_tasks_count: 2, overdue_tasks_count: 0, ai_intent: null, last_activity_at: '2026-05-29T10:00:00+05:00' },
    { id: 2098, title: 'Контейнер из Стамбула', client_name: 'Азиз Рахимов', stage: { id: 'UC_X2', label: 'TEL QOLDIRDI' }, amount: 4200000, currency: 'UZS', open_tasks_count: 1, overdue_tasks_count: 0, ai_intent: null, last_activity_at: '2026-05-27T16:00:00+05:00' },
  ],
  page: 1,
  per_page: 50,
  total: 3,
}

// ── M-5 AI fixtures (?mock=1 demos task → insight → ask-ai → draft) ──

export const mockAiInsight: AiInsightResponse = {
  ready: true,
  ai: {
    summary: 'Клиент тёплый, готов к отгрузке, но возражает по цене.',
    next_step: 'Предложить рассрочку оплаты и зафиксировать скидку 5%.',
    followup_when: 'завтра 11:00',
    followup_text: 'Бекзод, подготовил условия рассрочки — когда удобно созвониться?',
    intent: 'hot',
    intent_confidence: 0.87,
    tone: 'positive',
    tone_confidence: 0.74,
    objection: 'Цена выше ожиданий',
    generated_at: '2026-05-29T14:12:00+05:00',
    generated_by: 'ConversationAnalyst',
    reasoning: [
      'Клиент возражал по цене в последнем звонке',
      'Сравнивает с Cargo Express (конкурент)',
      'Сам упомянул готовность к рассрочке',
      'Срок отгрузки — на этой неделе',
    ],
  },
}

export const mockTaskDetail: TaskDetailResponse = {
  id: 1986,
  deal_id: 1986,
  manager_id: 928,
  title: 'Позвонить клиенту',
  description: 'Перезвонить по сборному грузу из Гуанчжоу, снять возражение по цене.',
  client_name: 'Бекзод Каримов',
  client_phone: '+998 90 123-45-67',
  client_telegram: 'bekzod_k',
  status: 'overdue',
  type: 'call',
  channel: 'telegram_business',
  deadline_iso: '2026-05-29T14:30:00+05:00',
  deadline_label: 'Сегодня 14:30',
  deadline_min_delta: -18,
  escalation_level: 2,
  stage: { id: 'UC_EYC73M', label: 'TG-MALUMOT', pipeline_id: '0', pipeline_name: 'MUROJAT', semantics: 'in_progress' },
  from_ai: true,
  task_source: 'conversation_analyst',
  why: 'Клиент написал в Telegram и ждёт ответа по тарифу.',
  director_note: null,
  ai: null,
  attachments: [
    { id: 'att_demo1', kind: 'task_complete', content_type: 'image/jpeg', url: 'https://picsum.photos/seed/ttn/240', thumbnail_url: 'https://picsum.photos/seed/ttn/120', size: 240000, name: 'ttn.jpg' },
  ],
  bitrix_sync_failed: false,
  card: {
    contact_name: 'Бекзод Каримов',
    cargo: [
      { key: 'UF_CRM_W', label: 'Габариты/вес', value: '0.2 куб · 156 кг · 500 шт · 10 коробок' },
      { key: 'UF_CRM_P', label: 'Цена', value: '1,6 $/кг' },
    ],
    notes: [
      { text: 'Цена и адрес отправлены клиенту 23.05. Ждём решение по отгрузке.', at: null, kind: 'deal' },
    ],
  },
}

export const mockDealDetail: DealDetailResponse = {
  id: 1986,
  title: 'Сборный груз Гуанчжоу',
  client_name: 'Бекзод Каримов',
  client_phone: '+998 90 123-45-67',
  client_telegram: 'bekzod_k',
  stage: { id: 'UC_EYC73M', label: 'TG-MALUMOT', pipeline_id: '0', pipeline_name: 'MUROJAT', semantics: 'in_progress' },
  pipeline_stages: [
    { id: 'NEW', label: 'MUROJAT', pipeline_id: '0', pipeline_name: 'MUROJAT', semantics: 'new' },
    { id: 'UC_2RLFLC', label: 'COMMENTLA', pipeline_id: '0', pipeline_name: 'MUROJAT', semantics: 'in_progress' },
    { id: 'UC_T4SS6O', label: 'MALUMOT SORALDI', pipeline_id: '0', pipeline_name: 'MUROJAT', semantics: 'in_progress' },
    { id: 'UC_EYC73M', label: 'TG-MALUMOT', pipeline_id: '0', pipeline_name: 'MUROJAT', semantics: 'in_progress' },
  ],
  amount: 18500000,
  currency: 'UZS',
  bitrix_url: 'https://example.bitrix24.ru/crm/deal/details/1986/',
  manager: { id: 928, name: 'Нафиса Собирова' },
  tabs: {
    tasks: {
      open: [
        { id: 1986, title: 'Позвонить клиенту', status: 'overdue', type: 'call', deadline_iso: '2026-05-29T14:30:00+05:00', deadline_label: 'Сегодня 14:30', escalation_level: 2, from_ai: true },
        { id: 1991, title: 'Отправить тариф', status: 'duesoon', type: 'message', deadline_iso: '2026-05-29T15:30:00+05:00', deadline_label: 'Сегодня 15:30', escalation_level: 0, from_ai: false },
      ],
      done_today: [],
      done_earlier: [],
    },
    history: [],
    ai: null,
  },
  card: {
    contact_name: 'Бекзод Каримов',
    cargo: [
      { key: 'UF_CRM_W', label: 'Габариты/вес', value: '0.2 куб · 156 кг · 500 шт · 10 коробок' },
      { key: 'UF_CRM_P', label: 'Цена', value: '1,6 $/кг' },
      { key: 'UF_CRM_N', label: 'Данные', value: 'Догружается в контейнер с другим грузом' },
    ],
    notes: [
      { text: 'Цена и адрес отправлены клиенту 23.05. Ждём решение по отгрузке.', at: null, kind: 'deal' },
    ],
  },
}

// ── M-6 slice D: calls ──────────────────────────────────────────────────────

export const mockCalls: CallsListResponse = {
  total: 3,
  summary: { avg_score: 7.3, by_sentiment: { positive: 2, neutral: 0, negative: 1 } },
  calls: [
    {
      id: 'cq-2048', deal_id: 1986, deal_client_name: 'Бекзод Каримов',
      manager: { id: 928, name: 'Нафиса Собирова' },
      uploaded_at: '2026-05-28T14:23:00+05:00', duration_seconds: 522,
      score_0_10: 8, score_0_100: 80, call_type: 'sales',
      sentiment: 'positive', client_intent: 'hot', status: 'ready',
    },
    {
      id: 'cq-2047', deal_id: 2014, deal_client_name: 'Дилшод Турсунов',
      manager: { id: 1026, name: 'Toxir Yusupov' },
      uploaded_at: '2026-05-28T11:08:00+05:00', duration_seconds: 312,
      score_0_10: 5, sentiment: 'negative', client_intent: 'cold', status: 'ready',
    },
    {
      id: 'cq-2046', deal_id: 2098, deal_client_name: 'ООО TashTrans Logistik',
      manager: { id: 928, name: 'Нафиса Собирова' },
      uploaded_at: '2026-05-27T16:40:00+05:00', duration_seconds: 0,
      score_0_10: null, sentiment: null, client_intent: null, status: 'analyzing',
    },
  ],
}

export const mockDealCalls: DealCallsResponse = {
  calls: [
    {
      id: 'cq-2048',
      uploaded_at: '2026-06-08T08:01:00Z',
      duration_seconds: 312,
      score_0_10: 8,
      sentiment: 'positive',
      client_intent: 'hot',
      client_interest: 'high',
      audio_url: 'https://download.samplelib.com/mp3/sample-9s.mp3',
      transcript: [
        { speaker: 'manager', text: 'Бекзод, добрый день! Звоню по поводу отгрузки.' },
        { speaker: 'client', text: 'Да, здравствуйте. Какие условия по рассрочке?' },
        { speaker: 'manager', text: 'Можем дать три месяца при предоплате 30%.' },
      ],
      has_speakers: true,
      feedback: 'Хорошо отработал возражение по цене. В следующий раз сразу назначайте время следующего шага.',
      objections: ['Цена', 'Сроки'],
      analyzed_at: '2026-05-28T14:25:00+05:00',
      language: 'ru',
      status: 'ready',
      fail_reason: null,
    },
  ],
}

export const mockDealAttachments: DealAttachmentsResponse = {
  attachments: [
    {
      id: 'att_mock01', content_type: 'image/jpeg',
      url: 'https://picsum.photos/seed/doc1/400',
      thumbnail_url: 'https://picsum.photos/seed/doc1/120',
      size: 240_000, name: 'nakladnaya.jpg',
      comment: 'Накладная по последней отгрузке', manager_id: 928,
      uploaded_at: '2026-06-08T09:12:00+05:00',
    },
    {
      id: 'att_mock02', content_type: 'application/pdf',
      url: 'https://example.com/contract.pdf', thumbnail_url: null,
      size: 88_000, name: 'dogovor.pdf',
      comment: null, manager_id: 928,
      uploaded_at: '2026-06-07T16:40:00+05:00',
    },
  ],
}

export const mockCallDetail: CallDetailResponse = {
  id: 'cq-2048', deal_id: 1986, deal_client_name: 'Бекзод Каримов',
  manager: { id: 928, name: 'Нафиса Собирова' },
  audio_url: 'https://download.samplelib.com/mp3/sample-9s.mp3',
  audio_expires_at: '2026-05-28T15:23:00+05:00',
  duration_seconds: 522, uploaded_at: '2026-05-28T14:23:00+05:00',
  analyzed_at: '2026-05-28T14:25:00+05:00',
  score_0_10: 8,
  score_0_100: 80,
  call_type: 'sales',
  criteria: [
    { key: 'opening', label: 'Открытие и контакт', weight: 10, score_0_10: 9, note: 'Представился, обозначил цель' },
    { key: 'discovery', label: 'Выявление потребности', weight: 20, score_0_10: 6, note: 'Не уточнил вес и габариты' },
    { key: 'presentation', label: 'Презентация решения', weight: 15, score_0_10: 8, note: 'Связал предложение с задачей' },
    { key: 'objections', label: 'Отработка возражений', weight: 15, score_0_10: 8, note: 'Снял возражение по цене' },
    { key: 'next_step', label: 'Следующий шаг', weight: 20, score_0_10: 5, note: 'Дата следующего контакта не зафиксирована' },
    { key: 'communication', label: 'Коммуникация и тон', weight: 10, score_0_10: 9, note: 'Говорил ясно, слушал' },
    { key: 'standards', label: 'Соответствие стандартам', weight: 10, score_0_10: 8, note: 'Стандарты соблюдены' },
  ],
  strengths: ['Чёткое представление и цель звонка', 'Снял возражение по цене фиксацией тарифа'],
  gaps: ['Не уточнил вес и габариты', 'Не зафиксировал дату следующего контакта'],
  feedback: 'В следующий раз запросите фото/размеры груза и назначьте конкретное время следующего шага.',
  client_interest: 'high', client_concerns: ['Цена выше, чем у конкурентов'],
  sentiment: 'positive', client_intent: 'hot',
  transcript: [
    { speaker: 'manager', text: 'Здравствуйте, Бекзод! Это Нафиса из Worldunicorns по вашему запросу.' },
    { speaker: 'client', text: 'Да, говорите.' },
    { speaker: 'manager', text: 'По вашему грузу можем отправить консолидацией, срок 18-20 дней.' },
    { speaker: 'client', text: 'Готов, но меня смущает цена, у других дешевле.' },
    { speaker: 'manager', text: 'Зафиксирую цену и пришлю расчёт в Telegram.' },
    { speaker: 'client', text: 'Хорошо, присылайте.' },
  ],
  has_speakers: true, status: 'ready', fail_reason: null,
}

// ── M-6 slice E: reactivation ──────────────────────────────────────────────

export const mockReactivation: ReactivationResponse = {
  deals: [
    {
      id: 2098, client_name: 'ООО «TashTrans Logistik»', amount_uzs: 12_500_000,
      stage: { id: 'UC_T4SS6O', label: 'MALUMOT SORALDI' },
      manager: { id: 928, name: 'Нафиса Собирова' },
      frozen_days: 7, last_activity_at: '2026-05-22T11:30:00+05:00',
      last_activity_text: 'Мы свяжемся позже',
      ai_draft_preview: 'Здравствуйте! По вашему запросу на доставку подготовил свежий расчёт…',
      ai_draft_full: null, skip_reason: null,
    },
    {
      id: 2076, client_name: 'Сардор Маннонов', amount_uzs: 4_200_000,
      stage: { id: 'UC_FWNCDJ', label: 'TEL_KOTARMADI' },
      manager: { id: 928, name: 'Нафиса Собирова' },
      frozen_days: 12, last_activity_at: '2026-05-17T09:10:00+05:00',
      last_activity_text: 'Не взял трубку, обещал перезвонить',
      ai_draft_preview: 'Сардор, добрый день! Хотел уточнить, актуальна ли ещё доставка груза…',
      ai_draft_full: null, skip_reason: null,
    },
  ],
}

// ── M-6 slice F: AI-report archive ─────────────────────────────────────────

export const mockReports: ArchiveReportsResponse = {
  total: 2,
  reports: [
    {
      id: 'leader_1_2026_05_29', kind: 'daily_leader_digest', title: 'Сводка за 29 мая',
      excerpt: 'Нафиса: 🔴 5 (+2), просрочки 3 · Toxir: 🔴 2, просрочки 0. Сегодня закрыто 23 задачи.',
      generated_at: '2026-05-29T09:00:00+05:00', tenant_id: 1,
    },
    {
      id: 'leader_1_2026_05_28', kind: 'daily_leader_digest', title: 'Сводка за 28 мая',
      excerpt: 'Команда закрыла 18 задач. 2 горячих лида без касания > 18ч — #1986 Бекзод, #2014 Дилшод.',
      generated_at: '2026-05-28T09:00:00+05:00', tenant_id: 1,
    },
  ],
}

// ── M-7 slice B: notification prefs ────────────────────────────────────────

export const mockNotificationPrefs: NotificationPrefs = {
  enabled: true,
  types: { overdue: true, new_task: true, escalation: false, ai_insights: true, leader_digest: true },
  quiet_hours: { enabled: true, from: '22:00', to: '10:00', respect_work_hours: true },
  grouping: 'five_min_window',
  snoozed_until: null,
}

// ── M-7 slice A: global search ─────────────────────────────────────────────

export const mockSearch: SearchResponse = {
  deals: [
    { id: 1986, client_name: 'Бекзод Каримов', stage_label: 'TG-MALUMOT', amount_uzs: 12_500_000 },
    { id: 2156, client_name: 'Бекзод Юнусов', stage_label: 'MUROJAT', amount_uzs: 4_200_000 },
  ],
  tasks: [
    { id: 1986, title: 'Позвонить Бекзоду Каримову', deal_id: 1986, status: 'overdue' },
  ],
  clients: [
    { name: 'Бекзод Каримов', phone: '+998901234567', deals_count: 2 },
  ],
}

export const mockReportDetail: ArchiveReportDetail = {
  id: 'leader_1_2026_05_29', kind: 'daily_leader_digest', title: 'Сводка за 29 мая',
  generated_at: '2026-05-29T09:00:00+05:00',
  metadata: { format: 'tg_html', window_days: 7 },
  content_md:
    '<b>AI-сводка дня · 29 мая</b>\n\n' +
    'Сегодня закрыто <b>23 задачи</b>. Лучше всех — <b>Нафиса</b> (8.4 avg).\n\n' +
    '<b>Риски</b>\n' +
    '#1986 Бекзод: горячий, без касания 18 ч\n' +
    '#2014 Дилшод: просрочка 2 дня\n\n' +
    '<b>Рекомендации</b>\n' +
    '<i>Начать день с #1986 — клиент готов к отгрузке.</i>',
}

// ── Director: AI-logic settings (?mock_role=director) ───────────────────────
export const mockAiSettings = {
  auto_execute_moves: false,
  ai_profile: {
    business: 'Логистический оператор — грузоперевозки Узбекистан–Китай. Хороший звонок: выявлен груз и зафиксирован следующий шаг.',
    tone: 'Деловой, но дружелюбный',
    reply_language: 'Узбекский или русский — как клиенту удобнее',
    glossary: ['FTL — полная загрузка фуры', 'LTL — догруз'],
    priorities: ['Скорость ответа клиенту'],
    forbidden: ['Обещать сроки доставки без подтверждения логиста'],
  },
  pipelines: [
    {
      pipeline_ext_id: '0',
      name: 'Продажи',
      is_default: true,
      stages: [
        {
          ext_id: 'NEW', name: 'Новая заявка',
          settings: {
            context: {
              purpose: 'Первичный контакт: понять запрос клиента и груз.',
              entry_conditions: 'Клиент оставил заявку или написал впервые.',
              exit_conditions: 'Уточнили маршрут и тип груза.',
            },
            transitions: [
              { to_pipeline: '0', to_stage: 'QUOTE', trigger: 'клиент назвал маршрут и груз', priority: 1 },
            ],
            script: { steps: [
              { n: 1, title: 'Поздороваться и представиться', required: true, description: null },
              { n: 2, title: 'Уточнить маршрут и тип груза', required: true, description: 'Откуда-куда, вес, габариты' },
            ] },
            stage_tasks: [],
          },
        },
        {
          ext_id: 'QUOTE', name: 'Расчёт ставки',
          settings: { context: null, transitions: [], script: null, stage_tasks: [] },
        },
        {
          ext_id: 'WON', name: 'Успешно',
          settings: { context: null, transitions: [], script: null, stage_tasks: [] },
        },
      ],
    },
  ],
}

export const mockRecommendations: RecommendationsResponse = {
  data_state: 'ok',
  computed_at: '2026-06-10T10:00:00+00:00',
  pipeline_name: 'MUROJAT',
  cards: [
    {
      stage_ext: 'SOTUV', stage_name: 'SOTUV', pipeline_ext: 'MUROJAT', kind: 'slow_stage', severity: 6.2,
      metrics: { median_hours: 148.8, sample_size: 12, funnel_median_hours: 24.0 },
      diagnosis_ru: 'Сделки висят в среднем 6.2 дн (по воронке — 1.0 дн, в 6.2× выше). 12 сделок зависли прямо сейчас.',
      recommendation_ru: 'Добавить авто-напоминание/задачу на 7-й день без движения.',
      suggested_action: { type: 'add_stage_task', day: 7 },
    },
    {
      stage_ext: 'CARGO', stage_name: 'CARGO', pipeline_ext: 'MUROJAT', kind: 'low_conversion', severity: 0.66,
      metrics: { entered: 40, progressed: 14, conversion_rate: 0.34 },
      diagnosis_ru: 'Дальше по воронке проходит только 34% (14 из 40).',
      recommendation_ru: 'Пересмотреть этап: добавить скрипт отработки или триггер перехода.',
      suggested_action: { type: 'review_stage' },
    },
  ],
}
